// Gantt: the grid with a timeline attached, like Smartsheet. Each row is a task; edit its dates in the grid or
// drag its bar. Subtasks indent under summary tasks, whose bars span their subtasks. Finish-to-start dependencies
// draw as arrows, and when a task slips the API moves everything after it.
import { useEffect, useMemo, useRef, useState } from "react";
import type { Field, Links, PersonRow, Row, Schedule, TableData, View, ViewConfig } from "./pApi";
import { pApi } from "./pApi";
import { GridView, type Item } from "./GridView";
import { addDays, daysBetween, filterRows, text, todayIso } from "./format";
import { SCALE, timeScale } from "./Timeline";

type Props = {
  data: TableData; view: View; people: PersonRow[]; me: string | null; search: string;
  onEdit: (rowId: string, values: Record<string, unknown>) => void;
  onBulk: (updates: { id: string; values: Record<string, unknown> }[], newRows?: Record<string, unknown>[]) => void;
  onAddRow: (opts?: { after?: string | null; parentId?: string | null; values?: Record<string, unknown> }) => void;
  onDeleteRows: (ids: string[]) => void; onOpen: (id: string) => void; onUndo: () => void;
  onPatchRow: (id: string, b: { parentId?: string | null }) => void;
  onConfig: (c: ViewConfig) => void; onWidth: (key: string, w: number) => void;
  onHeader: (f: Field, el: HTMLElement) => void; onAddChoice: (f: Field, name: string) => Promise<void>;
  onReload: () => void; fieldsOf: (id: string) => Field[] | undefined;
};

const RH = 34;
const DUR = "__duration";

export function GanttView(p: Props) {
  const s = p.data.schedule;
  if (!s) return <ScheduleSetup data={p.data} onDone={p.onReload} />;
  return <Gantt {...p} s={s} />;
}

function Gantt(p: Props & { s: Schedule }) {
  const { s, data } = p;
  const cfg = p.view.config;
  const zoom = cfg.zoom ?? "day";
  const px = SCALE[zoom];
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set(cfg.collapsed ?? []));
  const [selected, setSelected] = useState<string | null>(null);
  const left = useRef<HTMLDivElement | null>(null);
  const right = useRef<HTMLDivElement | null>(null);
  const syncing = useRef(false);
  const summaries = useMemo(() => new Set(data.summaries), [data.summaries]);
  const fStart = data.fields.find((f) => f.key === s.start)!, fEnd = data.fields.find((f) => f.key === s.end)!;
  const primary = data.fields.find((f) => f.primary) ?? data.fields[0];

  // Tree order: each task followed by its subtasks, skipping those under a collapsed summary. Filters keep a
  // match and lift it to the top level when its summary is filtered out.
  const { items, depth, hasKids } = useMemo(() => {
    const shown = new Set(filterRows(data.rows, cfg, data.fields, p.me, p.search, data.links).map((r) => r.id));
    const kids = new Map<string | null, Row[]>();
    for (const r of data.rows) { if (!shown.has(r.id)) continue; const par = r.parentId && shown.has(r.parentId) ? r.parentId : null; (kids.get(par) ?? kids.set(par, []).get(par)!).push(r); }
    for (const list of kids.values()) list.sort((a, b) => a.position - b.position);
    const out: Item[] = []; const depth = new Map<string, number>(); const hasKids = new Set<string>();
    const walk = (par: string | null, d: number) => {
      for (const r of kids.get(par) ?? []) {
        const withDur = { ...r, values: { ...r.values, [DUR]: durationOf(r, s) } };
        out.push({ kind: "row", row: withDur, depth: d }); depth.set(r.id, d);
        if (kids.get(r.id)?.length) { hasKids.add(r.id); if (!collapsed.has(r.id)) walk(r.id, d + 1); }
      }
    };
    walk(null, 0);
    return { items: out, depth, hasKids };
  }, [data.rows, data.fields, cfg, p.me, p.search, data.links, collapsed, s]);

  // Columns: the name, then the schedule fields, then anything else not hidden.
  const durField: Field = { id: DUR, key: DUR, name: "Days", type: "number", options: { decimals: 0 }, description: "Days from start to end, both included. Change it to move the end date.", position: 0, primary: false };
  const hidden = new Set(cfg.hidden ?? ganttDefaultHidden(data.fields, s));
  const keys = [s.start, s.end, DUR, s.assignee, s.progress, s.deps].filter(Boolean) as string[];
  const cols = [primary, ...keys.map((k) => (k === DUR ? durField : data.fields.find((f) => f.key === k))).filter((f): f is Field => !!f && !hidden.has(f.key)),
    ...data.fields.filter((f) => f !== primary && !keys.includes(f.key) && !hidden.has(f.key))];

  const edit = (rowId: string, values: Record<string, unknown>) => {
    if (DUR in values) {
      const r = data.rows.find((x) => x.id === rowId); const st = r?.values[s.start];
      const n = Number(String(values[DUR] ?? "").replace(/[^\d.-]/g, ""));
      if (!r || typeof st !== "string" || !Number.isFinite(n) || n < 1) return;
      p.onEdit(rowId, { [s.end]: addDays(st, Math.round(n) - 1) });
      return;
    }
    p.onEdit(rowId, values);
  };

  // ----- timeline range -----
  const today = todayIso();
  const [r0, r1] = useMemo(() => {
    let lo: string | null = null, hi: string | null = null;
    for (const r of data.rows) { const a = r.values[s.start], b = r.values[s.end] ?? a; if (typeof a === "string") { if (!lo || a < lo) lo = a; } if (typeof b === "string") { if (!hi || b > hi) hi = b; } }
    lo = lo && lo < today ? lo : today; hi = hi && hi > today ? hi : today;
    const pad = zoom === "day" ? 7 : zoom === "week" ? 14 : 30;
    let a = addDays(lo, -pad), b = addDays(hi, pad * 3);
    if (zoom !== "day") a = addDays(a, -((new Date(a + "T00:00:00Z").getUTCDay() + 6) % 7)); // start on a Monday
    if (daysBetween(a, b) < 900 / px) b = addDays(a, Math.ceil(900 / px));
    return [a, b];
  }, [data.rows, s, zoom, px, today]);
  const total = daysBetween(r0, r1) + 1;
  const xOf = (d: string) => daysBetween(r0, d) * px;

  // Scroll the two halves together, and open on today.
  const onLeft = (y: number) => { if (syncing.current) { syncing.current = false; return; } if (right.current && right.current.scrollTop !== y) { syncing.current = true; right.current.scrollTop = y; } };
  const onRight = () => { const y = right.current?.scrollTop ?? 0; if (syncing.current) { syncing.current = false; return; } if (left.current && left.current.scrollTop !== y) { syncing.current = true; left.current.scrollTop = y; } };
  useEffect(() => { if (right.current) right.current.scrollLeft = Math.max(0, xOf(today) - 160); }, [zoom]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id: string) => setCollapsed((c) => { const n = new Set(c); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  // Indent: under the task just above at the same level. Outdent: up to its summary's level.
  const indent = (id: string) => {
    const r = data.rows.find((x) => x.id === id); if (!r) return;
    const i = items.findIndex((x) => x.kind === "row" && x.row.id === id);
    for (let j = i - 1; j >= 0; j--) { const it = items[j]; if (it.kind !== "row") continue; const d = depth.get(it.row.id) ?? 0, mine = depth.get(id) ?? 0; if (d === mine) { p.onPatchRow(id, { parentId: it.row.id }); return; } if (d < mine) return; }
  };
  const outdent = (id: string) => { const r = data.rows.find((x) => x.id === id); if (!r?.parentId) return; const par = data.rows.find((x) => x.id === r.parentId); p.onPatchRow(id, { parentId: par?.parentId ?? null }); };

  // ----- dragging bars -----
  const [drag, setDrag] = useState<{ id: string; mode: "move" | "start" | "end" | "link"; x0: number; dx: number; to?: string | null } | null>(null);
  const startDrag = (e: React.MouseEvent, id: string, mode: "move" | "start" | "end" | "link") => {
    if (!data.table.canEdit) return;
    e.preventDefault(); e.stopPropagation();
    const st = { id, mode, x0: e.clientX, dx: 0, to: null as string | null };
    setDrag(st);
    const mm = (ev: MouseEvent) => {
      st.dx = ev.clientX - st.x0;
      if (mode === "link") { const el = (ev.target as HTMLElement).closest<HTMLElement>("[data-bar]"); st.to = el?.dataset.bar ?? null; }
      setDrag({ ...st });
    };
    const up = () => {
      document.removeEventListener("mousemove", mm); document.removeEventListener("mouseup", up);
      setDrag(null);
      const r = data.rows.find((x) => x.id === id); if (!r) return;
      if (mode === "link") {
        if (st.to && st.to !== id && s.deps) {
          const target = data.rows.find((x) => x.id === st.to); const cur = (target?.values[s.deps] as string[] | null) ?? [];
          if (target && !cur.includes(id) && !summaries.has(target.id)) p.onEdit(target.id, { [s.deps]: [...cur, id] });
        }
        return;
      }
      const days = Math.round(st.dx / px); if (!days) return;
      const a = r.values[s.start] as string | undefined, b = (r.values[s.end] as string | undefined) ?? undefined;
      if (!a) return;
      if (mode === "move") p.onEdit(id, { [s.start]: addDays(a, days), ...(b ? { [s.end]: addDays(b, days) } : {}) });
      else if (mode === "start") { const na = addDays(a, days); if (!b || na <= b) p.onEdit(id, { [s.start]: na }); }
      else if (mode === "end") { const nb = addDays(b ?? a, days); if (nb >= a) p.onEdit(id, { [s.end]: nb }); }
    };
    document.addEventListener("mousemove", mm); document.addEventListener("mouseup", up);
  };

  const { ticks, months } = useMemo(() => timeScale(r0, total, zoom, px), [r0, total, zoom, px]);

  const rowsShown = items.filter((i): i is Extract<Item, { kind: "row" }> => i.kind === "row");
  const yOf = new Map(rowsShown.map((it, i) => [it.row.id, i * RH + RH / 2]));
  const bars = rowsShown.map((it) => {
    const r = it.row; let a = r.values[s.start] as string | undefined; let b = (r.values[s.end] as string | undefined) ?? undefined;
    if (drag && drag.id === r.id && drag.mode !== "link" && a) {
      const days = Math.round(drag.dx / px);
      if (drag.mode === "move") { a = addDays(a, days); if (b) b = addDays(b, days); }
      else if (drag.mode === "start") a = addDays(a, days);
      else if (drag.mode === "end") b = addDays(b ?? a, days);
    }
    return { r, a, b };
  });
  const barAt = new Map(bars.map((x) => [x.r.id, x]));
  const arrows = s.deps ? bars.flatMap(({ r, a }) => ((r.values[s.deps!] as string[] | null) ?? []).map((pid) => {
    const pb = barAt.get(pid); if (!pb?.a || !a) return null;
    const x1 = xOf(pb.b ?? pb.a) + (pb.b ? px : 0), y1 = yOf.get(pid)!, x2 = xOf(a), y2 = yOf.get(r.id)!;
    const late = x2 < x1;
    const mid = x1 + 8;
    const d = late ? `M${x1} ${y1} H${mid} V${(y1 + y2) / 2} H${x2 - 10} V${y2} H${x2 - 2}` : `M${x1} ${y1} H${Math.max(mid, x2 - 10)} V${y2} H${x2 - 2}`;
    return { key: `${pid}>${r.id}`, d, late };
  }).filter((x): x is { key: string; d: string; late: boolean } => !!x)) : [];

  const label = (r: Row) => (primary ? text(primary, r.values[primary.key], data.links) : `#${r.number}`);
  const who = (r: Row) => (s.assignee ? (r.values[s.assignee] as { name?: string } | null)?.name : null);
  const prog = (r: Row) => (s.progress && typeof r.values[s.progress] === "number" ? Math.max(0, Math.min(100, r.values[s.progress] as number)) : null);

  return (
    <div className="pv-gantt" onKeyDown={(e) => {
      if (!(e.metaKey || e.ctrlKey) || !selected || !data.table.canEdit) return;
      if (e.key === "]") { e.preventDefault(); indent(selected); } else if (e.key === "[") { e.preventDefault(); outdent(selected); }
    }}>
      <div className="pv-gantt-bar">
        <div className="pv-seg" role="group" aria-label="Zoom">
          {(["day", "week", "month"] as const).map((z) => <button key={z} aria-pressed={zoom === z} onClick={() => p.onConfig({ zoom: z })}>{z === "day" ? "Days" : z === "week" ? "Weeks" : "Months"}</button>)}
        </div>
        <button className="btn" onClick={() => { if (right.current) right.current.scrollLeft = Math.max(0, xOf(today) - 160); }}>Today</button>
        {data.table.canEdit && <>
          <button className="btn" disabled={!selected} onClick={() => selected && indent(selected)} title="Make it a subtask of the task above (Ctrl+])">Indent</button>
          <button className="btn" disabled={!selected || !data.rows.find((r) => r.id === selected)?.parentId} onClick={() => selected && outdent(selected)} title="Move it up a level (Ctrl+[)">Outdent</button>
        </>}
        <span className="muted pv-gantt-hint">{data.table.canEdit ? "Drag a bar to move it, its ends to change dates, or the dot to another bar to link them." : "View only."}{s.auto !== false && s.deps ? " Tasks after a slipped task move with it." : ""}</span>
      </div>
      <div className="pv-gantt-split">
        <div className="pv-gantt-left">
          <GridView fields={cols} allFields={data.fields} items={items} links={data.links} people={p.people} canEdit={data.table.canEdit}
            widths={cfg.widths ?? {}} onWidth={p.onWidth} onEdit={edit} onBulk={p.onBulk} onAddRow={(o) => p.onAddRow(o)} onDeleteRows={p.onDeleteRows} onOpenRow={p.onOpen}
            onHeader={(f, el) => f.key !== DUR && p.onHeader(f, el)} onAddChoice={p.onAddChoice} onUndo={p.onUndo} fieldsOf={p.fieldsOf}
            readOnlyKeys={(r) => (hasKids.has(r.id) || summaries.has(r.id) ? [s.start, s.end, DUR, ...(s.progress ? [s.progress] : [])] : r.values[s.start] ? [] : [DUR])}
            tree={{ hasKids, collapsed, toggle }} virtual={false} scrollRef={left} onScrollY={onLeft} onSelectRow={setSelected}
            rowActions={(r) => [{ label: "Indent", run: () => indent(r.id) }, { label: "Outdent", run: () => outdent(r.id), disabled: !r.parentId }, { label: "Add subtask", run: () => p.onAddRow({ parentId: r.id }) }]}
            emptyHint={<span>No tasks yet. Add one below, or type into the grid.</span>} />
        </div>
        <div className="pv-gantt-right" ref={right} onScroll={onRight}>
          <div className="pv-tl" style={{ width: total * px, height: 34 + rowsShown.length * RH + (data.table.canEdit ? RH : 0) + 34 }}>
            <div className="pv-tl-head">
              {months.map((m, i) => <div key={i} className="pv-tl-month" style={{ left: m.x, width: m.w }}><span>{m.label}</span></div>)}
              {ticks.map((t, i) => <div key={i} className={`pv-tl-tick${t.weekend ? " we" : ""}`} style={{ left: t.x, width: zoom === "day" ? px : undefined }}>{t.label}</div>)}
            </div>
            <div className="pv-tl-body" style={{ height: rowsShown.length * RH }}>
              {zoom === "day" && ticks.filter((t) => t.weekend).map((t, i) => <div key={i} className="pv-tl-we" style={{ left: t.x, width: px }} />)}
              {ticks.map((t, i) => <div key={`l${i}`} className="pv-tl-line" style={{ left: t.x }} />)}
              <div className="pv-tl-today" style={{ left: xOf(today) + (zoom === "day" ? px / 2 : 0) }} title="Today" />
              {rowsShown.map((_, i) => <div key={i} className="pv-tl-row" style={{ top: i * RH }} />)}
              <svg className="pv-tl-arrows" width={total * px} height={rowsShown.length * RH} aria-hidden="true">
                <defs><marker id="pv-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M0 0L10 5L0 10z" fill="#7A8794" /></marker>
                  <marker id="pv-arrow-late" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto"><path d="M0 0L10 5L0 10z" fill="#C4314B" /></marker></defs>
                {arrows.map((a) => <path key={a.key} d={a.d} fill="none" stroke={a.late ? "#C4314B" : "#7A8794"} strokeWidth="1.25" markerEnd={`url(#${a.late ? "pv-arrow-late" : "pv-arrow"})`} />)}
                {drag?.mode === "link" && (() => { const b = barAt.get(drag.id); if (!b?.a) return null; const x = xOf(b.b ?? b.a) + px, y = yOf.get(drag.id)!; return <line x1={x} y1={y} x2={x + drag.dx} y2={y} stroke="var(--brand)" strokeWidth="1.5" strokeDasharray="4 3" />; })()}
              </svg>
              {bars.map(({ r, a, b }, i) => {
                if (!a) return null;
                const y = i * RH, isSum = hasKids.has(r.id) || summaries.has(r.id), milestone = !b && !isSum;
                const x = xOf(a), w = Math.max(px, (daysBetween(a, b ?? a) + 1) * px);
                const pc = prog(r);
                const name = label(r), person = who(r);
                if (milestone) return (
                  <div key={r.id}>
                    <div data-bar={r.id} className="pv-ms" style={{ left: x + px / 2 - 8, top: y + RH / 2 - 8 }} title={`${name}: ${a}`}
                      onMouseDown={(e) => startDrag(e, r.id, "move")} onDoubleClick={() => p.onOpen(r.id)} />
                    <span className="pv-bar-lab pv-ms-lab" style={{ left: x + px / 2 + 14, top: y + RH / 2 }}>{name}</span>
                  </div>
                );
                return (
                  <div key={r.id} data-bar={r.id} className={`pv-bar${isSum ? " sum" : ""}${selected === r.id ? " sel" : ""}${drag?.mode === "link" && drag.to === r.id ? " target" : ""}`}
                    style={{ left: x, width: w, top: y + (isSum ? 11 : 7) }} title={`${name}: ${a}${b ? ` to ${b}` : ""}${pc !== null ? ` · ${pc}% done` : ""}${person ? ` · ${person}` : ""}`}
                    onMouseDown={(e) => !isSum && startDrag(e, r.id, "move")} onDoubleClick={() => p.onOpen(r.id)}>
                    {!isSum && pc !== null && <span className="pv-bar-pc" style={{ width: `${pc}%` }} />}
                    {!isSum && data.table.canEdit && <><span className="pv-bar-h l" onMouseDown={(e) => startDrag(e, r.id, "start")} /><span className="pv-bar-h r" onMouseDown={(e) => startDrag(e, r.id, "end")} /></>}
                    {!isSum && data.table.canEdit && s.deps && <span className="pv-bar-dot" title="Drag to a task that can't start until this one finishes" onMouseDown={(e) => startDrag(e, r.id, "link")} />}
                    <span className="pv-bar-lab" style={{ left: w + 10 }}>{name}{person ? <span className="muted"> · {person}</span> : null}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** A Gantt shows the name and the schedule fields until someone picks other fields to show. */
export function ganttDefaultHidden(fields: Field[], s: Schedule): string[] {
  const keep = new Set([s.start, s.end, s.deps, s.assignee, s.progress].filter(Boolean));
  return fields.filter((f) => !f.primary && !keep.has(f.key)).map((f) => f.key);
}

function durationOf(r: Row, s: Schedule): number | null {
  const a = r.values[s.start], b = r.values[s.end];
  if (typeof a !== "string") return null;
  return typeof b === "string" ? daysBetween(a, b) + 1 : null;
}

/** Pick (or make) the start, end, predecessors, % complete and owner fields the Gantt runs on. */
export function ScheduleSetup({ data, onDone }: { data: TableData; onDone: () => void }) {
  const of = (types: string[]) => data.fields.filter((f) => types.includes(f.type));
  const dates = of(["date"]);
  const guess = (list: Field[], re: RegExp) => list.find((f) => re.test(f.name))?.key ?? "";
  const [st, setSt] = useState({ start: guess(dates, /start|begin|from/i) || dates[0]?.key || "", end: guess(dates, /end|finish|due|to\b/i) || dates[1]?.key || "", deps: guess(of(["link"]).filter((f) => f.options.table === data.table.id), /pred|depend|after/i), progress: guess(of(["percent", "number"]), /complete|progress|%/i), assignee: guess(of(["person"]), /owner|assign|who/i) });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      // Make what's missing: start and end dates, a Predecessors link to this table, % Complete.
      const make = async (name: string, type: Field["type"], options = {}) => (await pApi.addField(data.table.id, { name, type, options })).key;
      const start = st.start || await make("Start", "date");
      const end = st.end || await make("End", "date");
      const deps = st.deps === "__new" ? await make("Predecessors", "link", { table: data.table.id }) : st.deps;
      const progress = st.progress === "__new" ? await make("% Complete", "percent") : st.progress;
      await pApi.updateTable(data.table.id, { schedule: { start, end, deps: deps || undefined, progress: progress || undefined, assignee: st.assignee || undefined, auto: true } });
      onDone();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setBusy(false); }
  };
  const pick = (label: string, k: keyof typeof st, list: Field[], opts: { make?: string; none?: string }) => (
    <div className="field"><label htmlFor={`ss-${k}`}>{label}</label>
      <select id={`ss-${k}`} className="select" value={st[k]} onChange={(e) => setSt({ ...st, [k]: e.target.value })}>
        <option value="">{opts.none ?? `Add a ${label} field`}</option>
        {opts.make && <option value="__new">{opts.make}</option>}
        {list.map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
      </select>
    </div>
  );
  return (
    <div className="panel pad pv-setup" style={{ maxWidth: 560 }}>
      <h3>Set up the Gantt chart</h3>
      <p className="muted">Pick the fields each task's bar comes from. Anything missing is added for you.</p>
      {err && <div className="msg error">{err}</div>}
      {!data.table.canEdit ? <p className="muted">Ask the table's owner to set it up.</p> : <>
        {pick("Start", "start", dates, {})}
        {pick("End", "end", dates, {})}
        {pick("Predecessors", "deps", of(["link"]).filter((f) => f.options.table === data.table.id), { none: "No dependencies", make: "Add a Predecessors field" })}
        {pick("% Complete", "progress", of(["percent", "number"]), { none: "No progress", make: "Add a % Complete field" })}
        {pick("Assigned to", "assignee", of(["person"]), { none: "Nobody" })}
        <button className="btn primary" disabled={busy} onClick={save}>{busy ? "Setting up…" : "Show the Gantt chart"}</button>
      </>}
    </div>
  );
}
